import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { HeartHandshake, CheckCircle2, XCircle, PauseCircle, PlayCircle, Trash2, ExternalLink, MapPin, Mail, Clock } from "lucide-react";

const TABS = [
  { v: "pending_validation", l: "En attente" },
  { v: "active", l: "Actifs" },
  { v: "suspended", l: "Suspendus" },
  { v: "rejected", l: "Refusés" },
  { v: "", l: "Toutes" },
];

const STATUS_BADGE = {
  pending_validation: "bg-amber-100 text-amber-800",
  active: "bg-indigo-100 text-indigo-800",
  suspended: "bg-slate-200 text-slate-700",
  rejected: "bg-rose-100 text-rose-700",
};
const STATUS_LABEL = { pending_validation: "En attente", active: "Active", suspended: "Suspendue", rejected: "Refusée" };

export default function AdminEspaceClubs() {
  const [tab, setTab] = useState("pending_validation");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [commentFor, setCommentFor] = useState(null);
  const [comment, setComment] = useState("");

  const loadStats = () => api.get("/admin/clubs-espace/stats").then((r) => setStats(r.data || {})).catch(() => {});
  const load = async () => {
    setLoading(true);
    try {
      const p = tab ? `?status=${tab}` : "";
      const { data } = await api.get(`/admin/clubs-espace${p}`);
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { loadStats(); }, []);

  const decide = async (a, action, cmt = "") => {
    try {
      await api.post(`/admin/clubs-espace/${a.id}/decision`, { action, admin_comment: cmt });
      const msg = { validate: "Club validé et publié ✅", reject: "Demande refusée.", suspend: "Club suspendu.", reactivate: "Club réactivé." };
      toast.success(msg[action] || "Mise à jour effectuée.");
      setCommentFor(null); setComment("");
      load(); loadStats();
      window.dispatchEvent(new Event("admin:todos-refresh"));
    } catch (err) { toast.error(err?.response?.data?.detail || "Action impossible."); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement « ${a.name} » et tout son contenu ?`)) return;
    try { await api.delete(`/admin/clubs-espace/${a.id}`); toast.success("Club supprimé."); load(); loadStats(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible."); }
  };

  const filtered = items.filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()) || (a.city || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-clubs">
      <div className="flex items-center gap-2 mb-1">
        <HeartHandshake className="text-indigo-700" />
        <h1 className="font-heading text-2xl font-extrabold">Clubs</h1>
      </div>
      <p className="text-slate-500 text-sm">Validez les pages vitrines avant leur mise en ligne.</p>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { k: "pending", l: "En attente", c: "text-amber-600" },
          { k: "active", l: "Actifs", c: "text-indigo-600" },
          { k: "suspended", l: "Suspendus", c: "text-slate-500" },
          { k: "rejected", l: "Refusés", c: "text-rose-600" },
          { k: "total", l: "Total", c: "text-slate-900" },
        ].map((s) => (
          <div key={s.k} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-club-stat-${s.k}`}>
            <div className={`text-2xl font-extrabold ${s.c}`}>{stats[s.k] ?? 0}</div>
            <div className="text-xs text-slate-500">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.v || "all"} onClick={() => setTab(t.v)} data-testid={`admin-club-tab-${t.v || "all"}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${tab === t.v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
            {t.l}{t.v === "pending_validation" && stats.pending ? ` (${stats.pending})` : ""}
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="ml-auto max-w-xs" data-testid="admin-club-search" />
      </div>

      <div className="mt-5 space-y-3">
        {loading ? <div className="text-slate-500 text-sm">Chargement…</div> : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="admin-club-empty">Aucun club dans cette catégorie.</div>
        ) : filtered.map((a) => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-club-row-${a.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                {a.logo_url ? <img src={a.logo_url} alt="" className="w-full h-full object-cover" /> : <HeartHandshake className="text-slate-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{a.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_BADGE[a.status] || "bg-slate-100"}`}>{STATUS_LABEL[a.status] || a.status}</span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 text-indigo-700">{a.category_label}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                  {a.responsible_name && <span>Resp. {a.responsible_name}</span>}
                  {a.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {a.city}</span>}
                  {a.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {a.email}</span>}
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {(a.created_at || "").slice(0, 10)}</span>
                </div>
                {a.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{a.description}</p>}
                {a.admin_comment && <p className="text-xs text-slate-400 mt-1 italic">Note : {a.admin_comment}</p>}
              </div>
              {a.status === "active" && a.slug && (
                <a href={`/clubs/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-indigo-700 shrink-0" title="Voir la page"><ExternalLink size={16} /></a>
              )}
            </div>

            {commentFor === a.id ? (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Motif (visible par le club)…" data-testid="admin-club-comment" />
                <div className="mt-2 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setCommentFor(null); setComment(""); }}>Annuler</Button>
                  <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => decide(a, "reject", comment)} data-testid="admin-club-confirm-reject">Confirmer le refus</Button>
                </div>
              </div>
            ) : (
              <div className="mt-3 border-t border-slate-100 pt-3 flex flex-wrap gap-2">
                {a.status === "pending_validation" && (
                  <>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => decide(a, "validate")} data-testid={`admin-club-validate-${a.id}`}><CheckCircle2 size={15} className="mr-1" /> Valider & publier</Button>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setCommentFor(a.id); setComment(""); }} data-testid={`admin-club-reject-${a.id}`}><XCircle size={15} className="mr-1" /> Refuser</Button>
                  </>
                )}
                {a.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => decide(a, "suspend")} data-testid={`admin-club-suspend-${a.id}`}><PauseCircle size={15} className="mr-1" /> Suspendre</Button>
                )}
                {(a.status === "suspended" || a.status === "rejected") && (
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => decide(a, "reactivate")} data-testid={`admin-club-reactivate-${a.id}`}><PlayCircle size={15} className="mr-1" /> Réactiver</Button>
                )}
                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 ml-auto" onClick={() => remove(a)} data-testid={`admin-club-delete-${a.id}`}><Trash2 size={15} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
